import type {
  BatchRecord,
  DecisionPlan,
  LocationNode,
  ProductAnalysis,
  SupportContext,
  SupportIntent,
  SupportLocalAnswer,
  SupportServerStatus,
  WarehouseDataset,
} from "../types";
import {
  batchExpirySnapshot,
  expiryClassLabel,
  expiryUrgency,
  resolveBatchExpiry,
  shanghaiDateString,
} from "./batch";
import { WAREHOUSE_LOCATION_COUNT, WAREHOUSE_ZONE_COUNT } from "./warehouse";

const GENERIC_PRODUCT_WORDS = [
  "商品",
  "产品",
  "这款",
  "这个",
  "为什么",
  "怎么",
  "如何",
  "哪些",
  "是否",
  "可以",
  "现在",
  "当前",
  "一个",
  "仓序",
];

function normalise(value: string) {
  return value.toLowerCase().replace(/[\s\-_/.,，。、“”"'():：；;]+/g, "");
}

function formatNumber(value: number, digits = 1) {
  return Number.isFinite(value) ? value.toFixed(digits).replace(/\.0$/, "") : "—";
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

function formatDate(value: string) {
  if (!value) return "未提供";
  return value.replace(/-/g, "/");
}

function longestCommonSubstring(a: string, b: string) {
  let best = 0;
  let previous = new Array<number>(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i += 1) {
    const current = new Array<number>(b.length + 1).fill(0);
    for (let j = 1; j <= b.length; j += 1) {
      if (a[i - 1] === b[j - 1]) {
        current[j] = previous[j - 1] + 1;
        best = Math.max(best, current[j]);
      }
    }
    previous = current;
  }
  return best;
}

function findLocationCode(question: string) {
  return question.match(/\b([A-Z]\d{2,}[-_]\d{2,}[-_]\d{2,})\b/i)?.[1]?.replace(/_/g, "-").toUpperCase() ?? "";
}

function findProduct(question: string, plan: DecisionPlan) {
  const normalisedQuestion = normalise(question);
  const locationCode = findLocationCode(question);
  if (locationCode) {
    const byLocation = plan.products.find((item) => item.location.toUpperCase() === locationCode);
    if (byLocation) return byLocation;
  }
  const exactSku = plan.products.find((item) => normalisedQuestion.includes(normalise(item.sku)));
  if (exactSku) return exactSku;
  let best: { product: ProductAnalysis; score: number } | null = null;
  for (const product of plan.products) {
    const productName = normalise(product.product);
    const shared = longestCommonSubstring(normalisedQuestion, productName);
    const filtered = GENERIC_PRODUCT_WORDS.some((word) => normalisedQuestion.includes(word));
    const score = shared >= 4 || !filtered ? shared : 0;
    if (score >= 4 && (!best || score > best.score)) best = { product, score };
  }
  return best?.product;
}

function productFacts(product: ProductAnalysis) {
  const direction = product.slope > 0 ? "需求斜率向上" : product.slope < 0 ? "需求斜率向下" : "需求斜率接近持平";
  return [
    `SKU：${product.sku}`,
    `当前库位：${product.location || "未提供"}`,
    `热度分层：${product.category}`,
    `近 3 / 7 日出库均量：${formatNumber(product.avg3d)} / ${formatNumber(product.avg7d)} 件；热度排序值 ${formatNumber(product.recentHeat)} 件`,
    `回归斜率：${formatNumber(product.slope, 2)} 件/日（${formatPercent(product.slopePercent)}），${direction}`,
    `拟合度 R²：${formatNumber(product.r2, 2)}；波动率：${formatPercent(product.volatility * 100)}`,
    `预测日均：${formatNumber(product.forecastDaily)} 件，90% 区间 ${formatNumber(product.forecastLowerDaily)}–${formatNumber(product.forecastUpperDaily)} 件；补货周期：${product.recommendedCycle} 日`,
    `当前库存：${Math.round(product.currentStock)} 件；在途库存：${Math.round(product.incomingStock)} 件；可用天数：${product.stockDays > 900 ? "预测不足，无法可靠估算" : `${formatNumber(product.stockDays)} 天`}`,
    `识别周期：${product.periodDays} 日（信心 ${(product.periodConfidence * 100).toFixed(0)}%）；异常脉冲：${product.shockDays.length ? product.shockDays.join("、") : "未检出"}`,
    `证据置信度：${product.confidence}`,
  ].join("\n");
}

function topProductByCategory(plan: DecisionPlan, category: ProductAnalysis["category"]) {
  return plan.products.find((item) => item.category === category) ?? plan.products[0];
}

function batchFacts(dataset: WarehouseDataset, question: string, product?: ProductAnalysis) {
  const today = shanghaiDateString();
  const batches = dataset.batches
    .filter((batch) => !product || batch.sku === product.sku)
    .map((batch) => {
      const snapshot = batchExpirySnapshot(batch, today);
      return { batch, snapshot };
    })
    .filter((item) => item.snapshot.expiresOn)
    .sort(
      (a, b) =>
        expiryUrgency(a.snapshot.expiryClass) - expiryUrgency(b.snapshot.expiryClass)
        || a.snapshot.expiresOn.localeCompare(b.snapshot.expiresOn),
    );

  if (!batches.length) {
    return {
      tags: ["证据不足", "本次数据快照"],
      answer: `当前数据中没有可解析的批次效期。\n\n请至少提供批号与“到期日 / expiresOn”；生产日期和保质期天数为可选字段，用于更准确计算 ¼ / ⅓ / ½ 临期等级。${product ? `\n\n查询商品：${product.product}（${product.sku}）。` : ""}`,
    };
  }

  const requestedClass = /¼|四分之一|1\s*\/\s*4|quarter/i.test(question)
    ? "quarter"
    : /⅓|三分之一|1\s*\/\s*3|third/i.test(question)
      ? "third"
      : /½|二分之一|1\s*\/\s*2|half/i.test(question)
        ? "half"
        : null;
  const visible = requestedClass
    ? batches.filter(
        (item) =>
          !item.batch.recallFlag
          && expiryUrgency(item.snapshot.expiryClass) >= expiryUrgency(requestedClass)
          && item.snapshot.expiryClass !== "expired"
          && item.snapshot.expiryClass !== "unknown",
      )
    : batches;
  const lines = visible
    .slice(0, 8)
    .map(({ batch, snapshot }) => {
      const status = batch.recallFlag
        ? "召回冻结"
        : snapshot.expiryClass === "expired"
          ? "已过期"
          : snapshot.expiryClass === "unknown"
            ? "效期待核对"
            : expiryClassLabel(snapshot.expiryClass);
      return `• ${batch.product}｜批次 ${batch.lot || batch.id}｜到期 ${formatDate(snapshot.expiresOn)}｜${expiryClassLabel(snapshot.expiryClass)}｜${batch.quantity} 件｜${batch.location || "库位未提供"}｜${status}`;
    });
  const classCounts = (["quarter", "third", "half", "fresh"] as const)
    .map((value) => ({
      value,
      count: batches.filter((item) => !item.batch.recallFlag && item.snapshot.expiryClass === value).length,
    }));
  return {
    tags: [
      "本次数据快照",
      "批次规则",
      ...(batches.some((item) => item.batch.recallFlag) ? ["召回风险"] : []),
    ],
    answer: `临期等级按今天 ${formatDate(today)} 实时计算，库存数量仍以数据快照为准。\n\n各档可用批次数：${classCounts.map((item) => `${expiryClassLabel(item.value)} ${item.count}`).join("；")}。\n\n${lines.join("\n")}\n\n分配规则：排除召回、过期和待核对批次 → 按客户接受的 ¼ / ⅓ / ½ 等级筛选 → 同等级内 FEFO 最早到期优先。生产日期和保质期天数为可选字段。`,
  };
}

function placementFacts(plan: DecisionPlan, locations: LocationNode[], question: string, product?: ProductAnalysis) {
  const locationCode = findLocationCode(question);
  if (locationCode) {
    const node = locations.find((item) => item.code.toUpperCase() === locationCode);
    const occupant = plan.products.find((item) => item.location.toUpperCase() === locationCode);
    if (!node) {
      return {
        tags: ["证据不足", "本次数据快照"],
        answer: `当前库区中没有 ${locationCode}。请核对真实库位编码（如 A01-01-01）。`,
      };
    }
    return {
      tags: ["本次数据快照", "空间证据"],
      answer: [
        `库位 ${node.code}：${node.zone} 库区、${node.mainAisle} 号主通道、${node.smallAisle} 号小通道、${node.rack} 号货架，位于通道${node.side === "left" ? "左侧" : "右侧"}。原始编码 ${node.rawCode}，类型 ${node.locationType}。`,
        `按当前库位坐标到入口的几何距离估算为 ${formatNumber(node.distance, 0)} 米，仅用于相对排序，不等同于现场实测步行距离。`,
        occupant ? `当前数据中该库位关联商品：${occupant.product}（${occupant.sku}），热度 ${occupant.category}。` : "当前销售快照中没有商品关联到该库位。",
      ].join("\n\n"),
    };
  }

  const targetProduct = product ?? plan.products.find((item) => plan.placements.some((move) => move.sku === item.sku));
  if (targetProduct) {
    const move = plan.placements.find((item) => item.sku === targetProduct.sku);
    const currentNode = locations.find((item) => item.code === targetProduct.location);
    const targetNode = move ? locations.find((item) => item.code === move.to) : undefined;
    if (!move) {
      return {
        tags: ["本次数据快照", "空间证据"],
        answer: `「${targetProduct.product}」当前库位 ${targetProduct.location || "未提供"} 与建议拣货带的距离差异不足，系统没有为了制造动作而强制搬迁。\n\n热度：${targetProduct.category}；预测日均 ${formatNumber(targetProduct.forecastDaily)} 件；库位距离证据：${currentNode ? `约 ${formatNumber(currentNode.distance, 0)} 米` : "不足"}。`,
      };
    }
    return {
      tags: ["本次数据快照", "空间规则", "搬迁建议"],
      answer: [
        `「${targetProduct.product}」（${targetProduct.sku}）建议从 ${move.from} 迁往 ${move.to}。`,
        `依据：热度为 ${move.category}，预测日均 ${formatNumber(targetProduct.forecastDaily)} 件；当前库位${currentNode ? `距入口约 ${formatNumber(currentNode.distance, 0)} 米` : "距离证据不足"}，目标库位${targetNode ? `距入口约 ${formatNumber(targetNode.distance, 0)} 米` : "距离未提供"}。`,
        "这是基于当前库位坐标与需求预测的配置建议，尚未执行现场搬迁。导入更精确的仓库布局后，距离和路径会重新计算。",
      ].join("\n\n"),
    };
  }

  const moves = plan.placements.slice(0, 5);
  return {
    tags: ["本次数据快照", "空间规则"],
    answer: moves.length
      ? `当前有 ${plan.placements.length} 条距离改善建议，优先级最高的 ${moves.length} 条为：\n\n${moves
          .map((move) => `• ${move.product}：${move.from} → ${move.to}｜${move.category}｜${move.reason}`)
          .join("\n")}\n\n所有距离均来自当前库位坐标的几何估算，不代表已完成现场搬迁。`
      : "当前需求分层与库位距离带已经匹配，没有发现需要强制搬迁的品项。",
  };
}

function replenishmentFacts(
  dataset: WarehouseDataset,
  plan: DecisionPlan,
  product?: ProductAnalysis,
) {
  const target = product;
  if (target) {
    const order = plan.replenishments.find((item) => item.sku === target.sku);
    return {
      tags: ["本次数据快照", "补货规则", `置信度 ${target.confidence}`],
      answer: [
        `「${target.product}」按 ${target.recommendedCycle} 日周期补货。`,
        `依据：${target.category} 分层；预测日均 ${formatNumber(target.forecastDaily)} 件，90% 区间 ${formatNumber(target.forecastLowerDaily)}–${formatNumber(target.forecastUpperDaily)} 件；当前库存 ${Math.round(target.currentStock)} 件；在途 ${Math.round(target.incomingStock)} 件；预计可用 ${target.stockDays > 900 ? "无法可靠估算" : `${formatNumber(target.stockDays)} 天`}。`,
        order
          ? `建议本次补货 ${order.suggestedQuantity} 件，建议关注下单日 ${formatDate(order.nextOrderDate)}。`
          : "当前结果中没有生成该 SKU 的补货数量。",
        `补货数量 = 周期预测区间上界 − 现有库存 − 在途库存；识别周期 ${target.periodDays} 日（信心 ${(target.periodConfidence * 100).toFixed(0)}%）。当前数据快照截至 ${formatDate(dataset.meta.asOf)}，未连接采购系统，也不会自动下单。`,
      ].join("\n\n"),
    };
  }
  const urgent = [...plan.replenishments]
    .sort((a, b) => a.stockDays / a.cycle - b.stockDays / b.cycle)
    .slice(0, 7);
  return {
    tags: ["本次数据快照", "补货规则"],
    answer: `当前共有 ${plan.replenishments.length} 个 SKU 补货建议，风险优先级最高的为：\n\n${urgent
      .map((item) => `• ${item.product}｜${item.cycle} 日周期｜预测日均 ${formatNumber(item.forecastDaily)} 件｜90% 区间 ${formatNumber(item.forecastLowerDaily)}–${formatNumber(item.forecastUpperDaily)} 件｜建议 ${item.suggestedQuantity} 件｜下单日 ${formatDate(item.nextOrderDate)}｜置信度 ${item.confidence}`)
      .join("\n")}\n\n补货周期按热度选择，数量取周期预测区间上界并扣除现有与在途库存，不会直接写入采购系统。`,
  };
}

function trendFacts(dataset: WarehouseDataset, plan: DecisionPlan, product?: ProductAnalysis) {
  const target = product ?? topProductByCategory(plan, "爆旺");
  if (!target) {
    return {
      tags: ["证据不足"],
      answer: "当前没有足够的商品序列来计算回归趋势。请导入至少包含日期、SKU、商品名和数量字段的数据。",
    };
  }
  const direction =
    target.slope > 0.05
      ? "需求呈上升趋势"
      : target.slope < -0.05
        ? "需求呈下降趋势"
        : "趋势接近持平";
  const fit =
    target.r2 >= 0.55
      ? "回归线对波动的解释较强"
      : target.r2 >= 0.32
        ? "回归线可解释部分趋势，但仍需结合波动率"
        : "回归线解释力偏弱，不能仅凭斜率下结论";
  return {
    tags: ["本次数据快照", "单品回归", `置信度 ${target.confidence}`],
    answer: [
      `「${target.product}」当前被归为「${target.category}」，${direction}。`,
      `阻尼 Holt 结果：每过 1 天趋势变化 ${formatNumber(target.slope, 2)} 件（相对日均 ${formatPercent(target.slopePercent)}），R² ${formatNumber(target.r2, 2)}，波动率 ${formatPercent(target.volatility * 100)}；识别周期 ${target.periodDays} 日。`,
      `${fit}。爆旺/平销/平滞仅按近 3 日与近 7 日出库量排序，异常脉冲 ${target.shockDays.length ? target.shockDays.join("、") : "未检出"}。`,
      `数据覆盖 ${plan.quality.days} 天；这是当前导入样本内的经营趋势，不能替代市场因果或未来销量保证。`,
    ].join("\n\n"),
  };
}

function qualityFacts(dataset: WarehouseDataset, plan: DecisionPlan) {
  const locationsWithEvidence = new Set(plan.products.map((item) => item.location).filter((location) => location && location !== "UNASSIGNED")).size;
  return {
    tags: ["本次数据快照", "证据边界"],
    answer: [
      `当前数据：${plan.quality.rows.toLocaleString("zh-CN")} 条记录、${plan.products.length} 个 SKU、${plan.quality.days} 天跨度，关键字段完整度 ${(plan.quality.completeness * 100).toFixed(1)}%。`,
      `数据日期为 ${formatDate(dataset.meta.asOf)}；库存、出库和客户字段都以该快照为准。批次剩余效期会按今天 ${formatDate(shanghaiDateString())} 实时计算。`,
      `可识别库位 ${locationsWithEvidence} 个，可解析批次 ${dataset.batches.filter((batch) => Boolean(resolveBatchExpiry(batch))).length} 个。`,
      plan.quality.level === "good"
        ? "结论可用于滚动决策，但仍需结合现场盘点、客户约束和采购到货周期复核。"
        : plan.quality.level === "limited"
          ? "结论适合作为有限决策参考；建议补齐至少 21 天数据并检查缺失字段。"
          : "当前证据不足，不建议直接执行搬迁或大批量补货。",
      `主要限制：${plan.quality.notes.slice(0, 3).join(" ")}`,
    ].join("\n\n"),
  };
}

function helpFacts(dataset: WarehouseDataset) {
  return {
    tags: ["使用指南", "数据边界"],
    answer: [
      "你可以在顶部点击“导入数据”，或在“决策智能体”页导入 CSV / JSON。最关键字段是 date、sku、product、quantity；建议同时提供 location、lot、expiresOn、customer、currentStock。",
      `名称总汇收录 ${WAREHOUSE_LOCATION_COUNT.toLocaleString("zh-CN")} 个库位。标准库位编码 A01-01-01 依次表示 A 库区、01 号主通道、01 号小通道、01 号货架；货区页按“${WAREHOUSE_ZONE_COUNT} 个真实库区 → 主通道 → 小通道 → 货架 → 货物”五级下钻，特殊库位从独立入口直达库存。`,
      `当前${dataset.meta.source === "empty" ? "尚未接入业务数据" : dataset.meta.source === "file" ? "载入的是你导入的数据" : dataset.meta.source === "api" ? "载入的是后端数据" : "载入的是旧版演示数据"}，数据日期 ${formatDate(dataset.meta.asOf)}。${dataset.meta.source === "empty" ? "请导入 CSV / JSON，或等待后端接口接入。" : ""}`,
      "向客服提问时可以直接写商品名、SKU、库位或“客户接受 ¼ / ⅓ / ½ 临期等级”等约束；回答会先引用当前数据与规则，再说明证据缺口。",
    ].join("\n\n"),
  };
}

function generalFacts(dataset: WarehouseDataset, plan: DecisionPlan) {
  return {
    tags: ["本次数据快照", "决策摘要"],
    answer: [
      `当前数据快照截至 ${formatDate(dataset.meta.asOf)}，覆盖 ${plan.quality.days} 天、${plan.products.length} 个 SKU。`,
      `${plan.headline}；热度分布为爆旺 ${plan.hotCount}、平销 ${plan.stableCount}、平滞 ${plan.slowCount}，生成 ${plan.placements.length} 条空间配位建议和 ${plan.replenishments.length} 条补货建议。`,
      plan.risks.length ? `需要复核：${plan.risks.slice(0, 3).join(" ")}` : "当前未识别到期、召回或数据结构阻断项，但仍需核对现场库存。",
      "你可以指定 SKU、商品名、库位或客户效期约束，我会给出对应的数据证据和下一步动作。",
    ].join("\n\n"),
  };
}

function supportConfigFacts() {
  return {
    tags: ["系统说明", "证据边界"],
    answer: [
      "仓储客服采用双层回答：本地证据引擎先根据当前导入数据计算事实；配置了千帆服务端凭据时，再由千帆 Agent 做表达增强。",
      "即使千帆未配置或登录失效，商品趋势、库位、批次和补货问题仍会返回本地可核验答案，不会因模型不可用而中断。",
      "千帆密钥只保存在 Cloudflare 服务端环境变量中，不会进入浏览器。共享短链若要求百度登录，不能作为稳定生产鉴权，需使用应用 API 凭据或由业务方维护有效会话。",
      "当前回答不会声称已经完成搬迁、自动采购、仓库实时同步或人工客服在线。",
    ].join("\n\n"),
  };
}

function intentFor(question: string): SupportIntent {
  if (/千帆|接入|凭据|配置客服|agent\s*接通|服务端状态/i.test(question)) return "configuration";
  if (/导入|字段|格式|csv|json|布局文件|怎么用|使用说明/i.test(question)) return "help";
  if (/过期|效期|保质期|批次|召回|fefo|客户.*要求|至少.*天/i.test(question)) return "expiry";
  if (/补货|库存天数|下单|采购|每日补|7\s*日|30\s*日/i.test(question)) return "replenishment";
  if (/库位|区位|货位|摆放|布局|近端|远端|距离|门口|搬迁|过道|货架/i.test(question)) return "placement";
  if (/回归|爆火|爆旺|平销|平滞|斜率|r²|r2|趋势|热度|卖得/i.test(question)) return "trend";
  if (/数据.*(?:够|准|可靠|完整|质量)|置信|依据|缺失|能不能.*结论/i.test(question)) return "quality";
  if (/你好|能做什么|帮助|hi\b|hello/i.test(question)) return "help";
  return "general";
}

function buildContext(
  dataset: WarehouseDataset,
  plan: DecisionPlan,
  locations: LocationNode[],
  product: ProductAnalysis | undefined,
  intent: SupportIntent,
): SupportContext {
  const productPool = product
    ? [product]
    : intent === "trend" || intent === "replenishment"
      ? [
          ...plan.products.filter((item) => item.category === "爆旺").slice(0, 8),
          ...plan.products.filter((item) => item.category === "平销").slice(0, 4),
          ...plan.products.filter((item) => item.category === "平滞").slice(0, 4),
        ]
      : plan.products.slice(0, 12);
  const productSkus = new Set(productPool.map((item) => item.sku));
  const relevantLocationCodes = new Set<string>();
  if (product) {
    if (product.location && product.location !== "UNASSIGNED") relevantLocationCodes.add(product.location);
    plan.placements
      .filter((move) => move.sku === product.sku)
      .forEach((move) => {
        if (move.from && move.from !== "UNASSIGNED") relevantLocationCodes.add(move.from);
        if (move.to && move.to !== "UNASSIGNED") relevantLocationCodes.add(move.to);
      });
  }
  const batches = dataset.batches
    .filter((batch) => !product || batch.sku === product.sku)
    .map((batch) => ({ ...batch, expiresOn: resolveBatchExpiry(batch) }))
    .filter((batch) => batch.expiresOn)
    .sort((a, b) => a.expiresOn.localeCompare(b.expiresOn))
    .slice(0, product ? 20 : 12)
    .map((batch) => {
      const snapshot = batchExpirySnapshot(batch);
      return {
        sku: batch.sku,
        product: batch.product,
        lot: batch.lot,
        expiresOn: batch.expiresOn,
        quantity: batch.quantity,
        location: batch.location,
        recallFlag: batch.recallFlag,
        expiryClass: snapshot.expiryClass,
      };
    });
  return {
    snapshot: {
      warehouse: dataset.meta.warehouse,
      source: dataset.meta.source,
      asOf: dataset.meta.asOf,
      today: shanghaiDateString(),
      rows: plan.quality.rows,
      days: plan.quality.days,
      completeness: Number((plan.quality.completeness * 100).toFixed(1)),
      qualityLevel: plan.quality.level,
      qualityTitle: plan.quality.title,
      notes: plan.quality.notes.slice(0, 4),
    },
    counts: {
      products: plan.products.length,
      locations: locations.length,
      hot: plan.hotCount,
      stable: plan.stableCount,
      slow: plan.slowCount,
      placements: plan.placements.length,
      replenishments: plan.replenishments.length,
    },
    products: productPool.map((item) => ({
      sku: item.sku,
      product: item.product,
      category: item.category,
      location: item.location,
      averageDaily: Number(item.averageDaily.toFixed(2)),
      avg3d: Number(item.avg3d.toFixed(2)),
      avg7d: Number(item.avg7d.toFixed(2)),
      recentHeat: Number(item.recentHeat.toFixed(2)),
      slope: Number(item.slope.toFixed(3)),
      slopePercent: Number(item.slopePercent.toFixed(2)),
      r2: Number(item.r2.toFixed(3)),
      volatility: Number(item.volatility.toFixed(3)),
      forecastDaily: Number(item.forecastDaily.toFixed(2)),
      forecastLowerDaily: Number(item.forecastLowerDaily.toFixed(2)),
      forecastUpperDaily: Number(item.forecastUpperDaily.toFixed(2)),
      periodDays: item.periodDays,
      periodConfidence: Number(item.periodConfidence.toFixed(3)),
      shockDays: item.shockDays,
      cycle: item.recommendedCycle,
      suggestedQuantity: item.suggestedQuantity,
      currentStock: item.currentStock,
      incomingStock: item.incomingStock,
      stockDays: Number(Math.min(item.stockDays, 999).toFixed(1)),
      confidence: item.confidence,
      nextOrderDate: plan.replenishments.find((order) => order.sku === item.sku)?.nextOrderDate,
    })),
    placements: plan.placements
      .filter((move) => !product || productSkus.has(move.sku))
      .slice(0, 10),
    batches,
    relevantLocations: locations
      .filter((node) => !product || relevantLocationCodes.has(node.code))
      .slice(0, product ? 6 : 12)
      .map((node) => ({
        code: node.code,
        zone: node.zone,
        mainAisle: node.mainAisle,
        smallAisle: node.smallAisle,
        rack: node.rack,
        distance: Number(node.distance.toFixed(1)),
      })),
    rules: {
      requiredFields: ["date", "sku", "product", "quantity"],
      optionalFields: ["location", "lot", "expiresOn", "producedOn", "shelfLifeDays", "customer", "currentStock", "orders", "recallFlag"],
      cycleDays: { hot: 1, stable: 7, slow: 30 },
      batchOrder: "召回/过期/待核对排除 → ¼ / ⅓ / ½ 客户接受等级 → 同等级内 FEFO",
      placement: "爆旺近端、平销标准带、平滞远端；距离仅由库位坐标估算",
    },
  };
}

export function answerFromEvidence(
  question: string,
  dataset: WarehouseDataset,
  plan: DecisionPlan,
  locations: LocationNode[],
): SupportLocalAnswer {
  const cleaned = question.trim().slice(0, 1200);
  const intent = intentFor(cleaned);
  const product = findProduct(cleaned, plan);
  let result: { answer: string; tags: string[] };
  switch (intent) {
    case "configuration":
      result = supportConfigFacts();
      break;
    case "help":
      result = helpFacts(dataset);
      break;
    case "expiry":
      result = batchFacts(dataset, cleaned, product);
      break;
    case "placement":
      result = placementFacts(plan, locations, cleaned, product);
      break;
    case "replenishment":
      result = replenishmentFacts(dataset, plan, product);
      break;
    case "trend":
      result = trendFacts(dataset, plan, product);
      break;
    case "quality":
      result = qualityFacts(dataset, plan);
      break;
    default:
      result = product
        ? {
            answer: `${productFacts(product)}\n\n如果你要判断补货、库位或批次，请继续指定“补货数量”“当前库位”或“客户最低效期”。`,
            tags: ["本次数据快照", "单品证据", `置信度 ${product.confidence}`],
          }
        : generalFacts(dataset, plan);
  }
  return {
    answer: result.answer,
    tags: [...new Set(result.tags)],
    intent,
    suggestions: [
      "为什么这款商品被判为爆旺？",
      "当前库位为什么建议搬到近端？",
      "哪些批次临近过期？",
      "这款商品为什么按 7 日补货？",
      "我的数据是否足以支持这个结论？",
      "如何查看二维库区和导入商品数据？",
    ],
    context: buildContext(dataset, plan, locations, product, intent),
  };
}

export function buildHumanHandoffSummary(
  dataset: WarehouseDataset,
  plan: DecisionPlan,
  exchanges: Array<{ role: "user" | "assistant"; text: string }>,
) {
  const recent = exchanges
    .filter((message) => message.text.trim())
    .slice(-6)
    .map((message) => `${message.role === "user" ? "用户" : "客服"}：${message.text.trim().slice(0, 500)}`)
    .join("\n\n");
  return [
    "仓序仓储客服 · 转人工摘要",
    `仓库：${dataset.meta.warehouse}`,
    `数据快照：${dataset.meta.asOf}`,
    `数据质量：${plan.quality.title}（${(plan.quality.completeness * 100).toFixed(1)}% 完整度，${plan.quality.days} 天）`,
    `商品 / 库位 / 补货建议：${plan.products.length} / ${plan.placements.length} / ${plan.replenishments.length}`,
    `生成时间：${new Date().toISOString()}`,
    "",
    "近期对话：",
    recent || "尚未产生对话。",
    "",
    "说明：此摘要仅供人工接手时快速核对，不代表人工客服已在线或工单已创建。",
  ].join("\n");
}

const unavailableStatus: SupportServerStatus = {
  localReady: true,
  qianfan: {
    configured: false,
    mode: "unavailable",
    verified: false,
    detail: "未连接客服 API，当前使用本地证据引擎。",
  },
};

export async function getSupportStatus(signal?: AbortSignal): Promise<SupportServerStatus> {
  try {
    const response = await fetch("/api/support/chat", {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!response.ok) return unavailableStatus;
    return (await response.json()) as SupportServerStatus;
  } catch {
    return unavailableStatus;
  }
}

export async function requestSupportAgent(
  input: {
    message: string;
    local: SupportLocalAnswer;
    history: Array<{ role: "user" | "assistant"; content: string }>;
  },
  signal?: AbortSignal,
) {
  const response = await fetch("/api/support/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      message: input.message,
      localAnswer: input.local.answer,
      tags: input.local.tags,
      context: input.local.context,
      history: input.history.slice(-6),
    }),
    signal,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error?.message ?? `客服服务返回 ${response.status}`);
  }
  return payload as {
    mode: "qianfan" | "local";
    evidenceAnswer: string;
    agentAnswer: string | null;
    provider: SupportServerStatus["qianfan"];
    degradedReason?: string;
    requestId: string;
  };
}
